import React from "react";

/**
 * One palette for every status the Schedules / Devices / Rooms modules render,
 * so the three cannot drift into showing the same state in different colours.
 *
 * ⚠️ Unknown is a real, distinct state — it is NOT "off".
 *   - `Device.status` has no "unknown" value in its enum; unknown means the
 *     field is absent, and colouring that grey-as-offline tells an admin the
 *     device is offline when the truth is nobody knows.
 *   - `actions[].deviceOn` is nullable, and a null there means "leave this
 *     device alone", not "turn it off". Rendering it as OFF would misstate what
 *     the schedule is about to do.
 * Both fall through to the `unknown` swatch and the label "—" / "Unknown".
 */
const PALETTES = {
  scheduleStatus: {
    active: ["Active", "bg-emerald-50 text-emerald-700 ring-emerald-600/20"],
    pause: ["Paused", "bg-amber-50 text-amber-700 ring-amber-600/20"],
    draft: ["Draft", "bg-slate-100 text-slate-600 ring-slate-500/20"],
    archived: ["Archived", "bg-slate-100 text-slate-500 ring-slate-400/20"],
  },
  cronStatus: {
    active: ["Ready to run", "bg-emerald-50 text-emerald-700 ring-emerald-600/20"],
    processing: ["Processing", "bg-blue-50 text-blue-700 ring-blue-600/20"],
    reprocessing: ["Reprocessing", "bg-blue-50 text-blue-700 ring-blue-600/20"],
    // A spent one-time schedule. It can never fire again — not an error.
    completed: ["Completed", "bg-indigo-50 text-indigo-700 ring-indigo-600/20"],
    disabled: ["Disabled", "bg-slate-100 text-slate-600 ring-slate-500/20"],
    paused: ["Paused", "bg-amber-50 text-amber-700 ring-amber-600/20"],
    failed: ["Failed", "bg-red-50 text-red-700 ring-red-600/20"],
    draft: ["Draft", "bg-slate-100 text-slate-600 ring-slate-500/20"],
  },
  // Subscription signal. `red` blocks schedule MUTATION; it does not stop
  // existing schedules from executing, so never label it "stopped".
  signal: {
    green: ["Subscribed", "bg-emerald-50 text-emerald-700 ring-emerald-600/20"],
    yellow: ["Expiring", "bg-amber-50 text-amber-700 ring-amber-600/20"],
    red: ["Expired", "bg-red-50 text-red-700 ring-red-600/20"],
  },
  deviceStatus: {
    online: ["Online", "bg-emerald-50 text-emerald-700 ring-emerald-600/20"],
    offline: ["Offline", "bg-red-50 text-red-700 ring-red-600/20"],
  },
  powerState: {
    true: ["On", "bg-emerald-50 text-emerald-700 ring-emerald-600/20"],
    false: ["Off", "bg-slate-100 text-slate-600 ring-slate-500/20"],
  },
  isAdded: {
    true: ["Added", "bg-indigo-50 text-indigo-700 ring-indigo-600/20"],
    false: ["Available", "bg-slate-100 text-slate-600 ring-slate-500/20"],
  },
};

const UNKNOWN = ["Unknown", "bg-slate-50 text-slate-400 ring-slate-300/40"];

const SIZES = {
  sm: "text-[11px] px-2 py-0.5",
  md: "text-xs px-2.5 py-1",
};

export default function StatusBadge({
  kind,
  value,
  size = "sm",
  unknownLabel,
  title,
  // In a table the column header says which status this is. Standing alone —
  // a modal header, a detail card — it does not, so pass a caption.
  label,
}) {
  const palette = PALETTES[kind] || {};
  const key = value === null || value === undefined ? "__unknown" : String(value);
  // Named paletteLabel, not label — `label` is the caption prop above.
  const [paletteLabel, classes] = palette[key] || UNKNOWN;

  const isUnknown = !palette[key];
  const text = isUnknown ? (unknownLabel ?? paletteLabel) : paletteLabel;

  const badge = (
    <span
      title={title || (isUnknown ? "No value reported" : undefined)}
      className={`inline-flex items-center rounded-full font-semibold ring-1 ring-inset whitespace-nowrap ${classes} ${SIZES[size] || SIZES.sm}`}
    >
      {text}
    </span>
  );

  if (!label) return badge;

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {badge}
    </span>
  );
}
